import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { LoginModal } from '../components/auth/LoginModal';
import { RegisterModal } from '../components/auth/RegisterModal';
import { EmailVerificationModal } from '../components/auth/EmailVerificationModal';
import { PasswordResetModal } from '../components/auth/PasswordResetModal';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../store/authStore';
import {
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  CalculatorIcon,
  BoltIcon,
  ShieldCheckIcon,
  CloudArrowUpIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ClockIcon,
  BanknotesIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';

// Unsplash royalty-free images for travel theme
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&q=80', // Travel map with passport
  explore: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80', // Eiffel Tower from Trocadéro
  planning: 'https://images.unsplash.com/photo-1452421822248-d4c2b47f0c81?w=800&q=80', // Travel planning
};

export const LandingPage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Ensure dark mode class is removed on landing page (light theme only)
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleRegistrationSuccess = (email: string) => {
    setShowRegister(false);
    setRegisteredEmail(email);
    setShowEmailVerification(true);
  };

  const handleEmailVerificationSuccess = () => {
    setShowEmailVerification(false);
    setShowLogin(true);
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordReset(false);
    setShowLogin(true);
  };

  // Core Features
  const coreFeatures = [
    {
      icon: MapPinIcon,
      title: 'Destination Explorer',
      description:
        'Explore 49+ curated destinations with detailed profiles — weather, points of interest, photos, safety info, and costs. Add your own custom destinations with automatic AI enrichment.',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'AI Travel Advisor',
      description:
        'Get AI-powered answers to any travel question. Ask about destinations, visa requirements, local customs, safety tips, packing lists, and more.',
    },
    {
      icon: CalculatorIcon,
      title: 'Travel Calculators',
      description:
        'Five essential calculators: currency converter, trip budget estimator, tip calculator, timezone converter, and distance calculator with geocoding support.',
    },
  ];

  // Calculator Features
  const calculatorFeatures = [
    {
      icon: CurrencyDollarIcon,
      title: 'Currency Converter',
      description: 'Convert between 160+ world currencies with real-time exchange rates.',
    },
    {
      icon: MapPinIcon,
      title: 'Trip Budget Estimator',
      description: 'Estimate daily costs for any destination based on your travel style and budget level.',
    },
    {
      icon: BanknotesIcon,
      title: 'Tip Calculator',
      description: 'Calculate tips with country-specific customs and local tipping etiquette.',
    },
    {
      icon: ClockIcon,
      title: 'Timezone Converter',
      description: 'Convert times between any timezones with daylight saving awareness.',
    },
    {
      icon: ArrowsRightLeftIcon,
      title: 'Distance Calculator',
      description: 'Calculate distances between destinations with geocoding and km/miles support.',
    },
  ];

  const benefits = [
    {
      icon: BoltIcon,
      title: 'Lightning Fast',
      description: 'Get instant answers and calculations powered by advanced AI technology.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Secure & Private',
      description: 'Your travel data is protected with enterprise-grade security.',
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Always Available',
      description: 'Access your travel tools anytime, anywhere, on any device.',
    },
    {
      icon: GlobeAltIcon,
      title: '5 Languages',
      description: 'Full support for English, Spanish, French, German, and Italian.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-cyan-50/90 backdrop-blur-md border-b border-cyan-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="/logo_light.png"
                alt="Auxilio"
                className="h-12"
              />
            </div>

            {/* Auth Buttons */}
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setShowLogin(true)}>
                Login
              </Button>
              <Button onClick={() => setShowRegister(true)}>Register</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Background Image */}
      <section className="relative pt-20 pb-20 px-4 sm:px-6 lg:px-8 min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.hero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/95 via-cyan-800/85 to-teal-700/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              {/* Title */}
              <div className="mb-6 flex items-center">
                <GlobeAltIcon className="h-16 sm:h-20 lg:h-24 w-16 sm:w-20 lg:w-24 text-cyan-300 mr-4" />
                <div>
                  <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">Auxilio</span>
                  <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cyan-300 ml-3">Travel</span>
                </div>
              </div>

              <p className="text-xl sm:text-2xl text-cyan-100 mb-6 max-w-xl">
                Your AI Travel Planning Companion
              </p>

              <p className="text-lg text-cyan-200 mb-8 max-w-lg">
                Explore 49+ destinations, get AI-powered travel advice, convert currencies,
                estimate budgets, and plan every detail of your next trip — all in one place.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button size="lg" onClick={() => setShowRegister(true)} className="text-lg px-8 py-4 bg-cyan-500 hover:bg-cyan-600">
                  Get Started Free
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => setShowLogin(true)}
                  className="text-lg px-8 py-4 bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Login to Your Account
                </Button>
              </div>
            </div>

            {/* Hero Image Card */}
            <div className="hidden lg:block">
              <div className="relative">
                <img
                  src={IMAGES.explore}
                  alt="Travel exploration"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-cyan-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">9</p>
                  <p className="text-sm">Travel Tools</p>
                </div>
                <div className="absolute -top-6 -right-6 bg-teal-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">5</p>
                  <p className="text-sm">Languages</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-cyan-100 p-3 rounded-full mr-4">
                <GlobeAltIcon className="h-8 w-8 text-cyan-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                AI-Powered Travel Tools
              </h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to plan your perfect trip, powered by advanced AI technology.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coreFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-cyan-100 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.planning})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <div className="absolute inset-0 bg-gray-100/95" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-teal-100 p-3 rounded-full mr-4">
                <CalculatorIcon className="h-8 w-8 text-teal-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Travel Calculator Suite
              </h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Five essential calculators for every traveler — from currency conversion and budget planning to distance and timezone tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {calculatorFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-teal-100 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-teal-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Auxilio Travel?
            </h2>
            <p className="text-xl text-gray-600">
              Built for speed, security, and ease of use
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-cyan-50 p-4 rounded-full">
                    <benefit.icon className="h-8 w-8 text-cyan-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-600 to-teal-800 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <GlobeAltIcon className="h-20 w-20 text-white/90" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Plan Your Next Adventure?
          </h2>
          <p className="text-xl text-cyan-100 mb-8">
            Join travelers who trust Auxilio Travel for their travel planning needs.
            Free to register, with no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <button
              onClick={() => setShowRegister(true)}
              className="rounded-lg font-semibold text-lg px-8 py-4 bg-white text-cyan-700 hover:bg-gray-100 shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
            >
              Create Free Account
            </button>
            <button
              onClick={() => setShowLogin(true)}
              className="rounded-lg font-medium text-lg px-8 py-4 border-2 border-white text-white bg-transparent hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center items-center space-x-2 mb-4">
            <GlobeAltIcon className="h-8 w-8 text-cyan-400" />
            <span className="text-xl font-bold text-white">Auxilio Travel</span>
          </div>
          <p className="text-gray-400 text-sm">
            AI-Powered Travel Planning Companion
          </p>
          <p className="text-gray-500 text-xs mt-4 max-w-2xl mx-auto">
            Auxilio Travel provides travel information and tools for general guidance purposes.
            Travel conditions, regulations, and prices can change. Always verify details with official sources before traveling.
          </p>
          <p className="text-gray-600 text-xs mt-4">
            Images provided by <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">Unsplash</a>
          </p>
          <p className="text-gray-500 text-xs mt-2">
            &copy; 2026 Auxilio Technologies LLC. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
        onSwitchToPasswordReset={() => {
          setShowLogin(false);
          setShowPasswordReset(true);
        }}
      />

      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSuccess={handleRegistrationSuccess}
      />

      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        onSuccess={handleEmailVerificationSuccess}
        email={registeredEmail}
      />

      <PasswordResetModal
        isOpen={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
        onSuccess={handlePasswordResetSuccess}
        onSwitchToLogin={() => {
          setShowPasswordReset(false);
          setShowLogin(true);
        }}
      />
    </div>
  );
};
